import { ApolloError } from '@apollo/client';
import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  ExamCenterProctorType,
  IdType,
  SearchExamCenterProctorsDocument,
  useCreateExamCenterProctorMutation,
} from '@exam-center/graphql';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { ProctorType } from '../../containers';
import { useUploadThirdProctorFiles } from '../../hooks';
import { useProctoringDetailsModal } from '../proctoring-details-modal-provider';

import {
  type BaseCreateThirdProctorModalFormInput,
  BaseCreateThirdProctorModalFormSchema,
} from './schema';

export const EXAM_CENTER_PROCTOR_ERROR_CODE_TO_FORM_ERROR = {
  EXAM_CENTER_PROCTOR_BY_ID_CARD_ALREADY_EXISTS: {
    fieldName: 'personal.idNumber',
    message: 'เลขบัตรประชาชนนี้มีอยู่ในระบบแล้ว กรุณาตรวจสอบหรือใช้หมายเลขบัตรประชาชนอื่น',
  },
  EXAM_CENTER_PROCTOR_REQUEST_BY_ID_CARD_IS_IN_REVIEW: {
    fieldName: 'personal.idNumber',
    message: 'เลขบัตรประชาชนนี้อยู่ระหว่างการยื่นคำขออนุมัติ กรุณาตรวจสอบ',
  },
  INVALID_CITIZEN_CARD_NUMBER: {
    fieldName: 'personal.idNumber',
    message: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น',
  },
  EXAM_CENTER_PROCTOR_BY_EMAIL_ALREADY_EXISTS: {
    fieldName: 'personal.email',
    message: 'อีเมลนี้ถูกใช้งานแล้วโดยผู้คุมสอบประเภทที่ 1 หรือ 2 ในระบบ',
  },
} as const;

export default function CreateThirdProctorModalFormProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;
  const { examEvent } = useExamEventDetails();
  const { showSnackbar } = useSnackbar();
  const { setAction } = useProctoringDetailsModal();
  const [createExamCenterProctor] = useCreateExamCenterProctorMutation({
    onCompleted: () => {
      showSnackbar({ message: 'เพิ่มผู้คุมสอบประเภทที่ 3 เรียบร้อยแล้ว', variant: 'success' });
      setAction({ action: 'add-proctor', data: { proctorType: ProctorType.Proctor_3 } });
    },
    onError: (error) => {
      if (error instanceof ApolloError) {
        const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
        const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
        const errorCodes = extensionDetails.map((detail) => detail.code);
        const formErrors = errorCodes
          .map(
            (code) =>
              EXAM_CENTER_PROCTOR_ERROR_CODE_TO_FORM_ERROR[
                code as keyof typeof EXAM_CENTER_PROCTOR_ERROR_CODE_TO_FORM_ERROR
              ],
          )
          .filter(Boolean);
        for (const formError of formErrors) {
          formContext.setError(formError.fieldName, {
            type: 'manual',
            message: formError.message,
          });
        }
      }
    },
    refetchQueries: [SearchExamCenterProctorsDocument],
  });
  const { handleUpload } = useUploadThirdProctorFiles();

  const formContext = useForm<BaseCreateThirdProctorModalFormInput>({
    resolver: zodResolver(BaseCreateThirdProctorModalFormSchema),

    defaultValues: {
      personal: {
        idNumber: '',
        titleTh: null,
        firstNameTh: '',
        lastNameTh: '',
        email: null,
      },
      document: {
        agreementForms: [],
        identifications: [],
      },
    },
  });

  const onSubmit = async (data: BaseCreateThirdProctorModalFormInput) => {
    const toFiles = (
      items?:
        | (File | { type: string; url: string; name: string; key: string; size: number } | null)[]
        | null,
    ) => (items ? items.filter((i): i is File => i instanceof File) : null);
    const idNumber = data.personal?.idNumber?.replace(/-/g, '');
    const agreementFileNames =
      (await handleUpload(toFiles(data.document?.agreementForms ?? null))) ?? [];
    const identificationFileNames =
      (await handleUpload(toFiles(data.document?.identifications ?? null))) ?? [];

    await createExamCenterProctor({
      variables: {
        createExamCenterProctorInput: {
          titleTh: data.personal?.titleTh?.value ?? '',
          personalInfoFileNames: identificationFileNames,
          middleNameTh: null,
          lastNameTh: data.personal?.lastNameTh ?? '',
          idType: IdType.CitizenCard,
          idNumber: idNumber ?? '',
          firstNameTh: data.personal?.firstNameTh ?? '',
          examEventId: examEvent.id,
          examCenterProctorType: ExamCenterProctorType.Proctor_3,
          examCenterCode: examEvent.examCenterCode,
          email: data.personal?.email ?? null,
          agreementFileNames: agreementFileNames,
        },
      },
    });
  };
  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>{children}</Form>
    </FormProvider>
  );
}
